import { Fragment, useEffect, useState } from "react";
import type { Category } from "@ai-pos/shared";
import { categoriesApi } from "../api/resources.js";
import { useAuthStore } from "../stores/authStore.js";

interface FormState {
  id: string | null;
  name: string;
  sortOrder: number;
  isActive: boolean;
  parentCategoryId: string | null;
  /** 新規作成時のみ使用。現在の店舗以外に同じカテゴリーを複製する対象店舗ID。 */
  targetStoreIds: string[];
}

export function CategoryAdmin() {
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const accessibleStores = useAuthStore((state) => state.accessibleStores);
  const otherStores = accessibleStores.filter((s) => s.id !== activeStoreId);
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    categoriesApi
      .list()
      .then((res) => setCategories(res.categories))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
  };

  useEffect(load, []);

  const topLevelCategories = categories
    .filter((c) => !c.parentCategoryId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const childrenOf = (parentId: string) =>
    categories.filter((c) => c.parentCategoryId === parentId).sort((a, b) => a.sortOrder - b.sortOrder);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
        parentCategoryId: form.parentCategoryId,
      };
      if (form.id) {
        await categoriesApi.update(form.id, payload);
      } else {
        const res = await categoriesApi.create(payload);
        if (form.targetStoreIds.length > 0) {
          await categoriesApi.copyToStores(res.category.id, form.targetStoreIds);
        }
      }
      setForm(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const openCreate = (parentCategoryId: string | null) =>
    setForm({ id: null, name: "", sortOrder: categories.length, isActive: true, parentCategoryId, targetStoreIds: [] });

  const openEdit = (category: Category) =>
    setForm({
      id: category.id,
      name: category.name,
      sortOrder: category.sortOrder,
      isActive: category.isActive,
      parentCategoryId: category.parentCategoryId,
      targetStoreIds: [],
    });

  const toggleTargetStore = (storeId: string, checked: boolean) => {
    if (!form) return;
    setForm({
      ...form,
      targetStoreIds: checked ? [...form.targetStoreIds, storeId] : form.targetStoreIds.filter((id) => id !== storeId),
    });
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm(`「${category.name}」を削除します。よろしいですか？`)) return;
    setError(null);
    try {
      await categoriesApi.remove(category.id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "削除に失敗しました");
    }
  };

  const editingHasChildren = !!form?.id && childrenOf(form.id).length > 0;

  const renderRow = (category: Category, isChild: boolean) => (
    <tr key={category.id}>
      <td>{isChild ? <span style={{ paddingLeft: 24, color: "var(--color-text-muted)" }}>└ {category.name}</span> : category.name}</td>
      <td>{category.sortOrder}</td>
      <td>
        {category.isActive ? <span className="badge">有効</span> : <span className="badge badge-danger">停止</span>}
      </td>
      <td>
        {!isChild && (
          <button className="btn" onClick={() => openCreate(category.id)}>
            サブカテゴリー追加
          </button>
        )}
        <button className="btn" onClick={() => openEdit(category)}>
          編集
        </button>
        <button className="btn btn-danger btn-outline" onClick={() => void handleDelete(category)}>
          削除
        </button>
      </td>
    </tr>
  );

  return (
    <div className="stack">
      <div className="row">
        <h2 style={{ flex: 1 }}>カテゴリー管理</h2>
        <button className="btn btn-primary" onClick={() => openCreate(null)}>
          新規追加
        </button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>カテゴリー名</th>
              <th>並び順</th>
              <th>状態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {topLevelCategories.map((category) => (
              <Fragment key={category.id}>
                {renderRow(category, false)}
                {childrenOf(category.id).map((child) => renderRow(child, true))}
              </Fragment>
            ))}
            {categories.length === 0 && (
              <tr>
                <td colSpan={4} className="empty-state">
                  カテゴリーが登録されていません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="modal-backdrop" onClick={() => setForm(null)}>
          <div className="modal stack" onClick={(e) => e.stopPropagation()}>
            <h3>{form.id ? "カテゴリー編集" : "新規カテゴリー"}</h3>
            <div className="form-field">
              <label htmlFor="cat-name">カテゴリー名</label>
              <input id="cat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-field">
              <label htmlFor="cat-parent">親カテゴリー</label>
              <select
                id="cat-parent"
                value={form.parentCategoryId ?? ""}
                disabled={editingHasChildren}
                onChange={(e) => setForm({ ...form, parentCategoryId: e.target.value || null })}
              >
                <option value="">（なし・トップレベルのカテゴリー）</option>
                {topLevelCategories
                  .filter((c) => c.id !== form.id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
              {editingHasChildren && (
                <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: "4px 0 0" }}>
                  サブカテゴリーを持つカテゴリーは、他のカテゴリーの子にはできません。
                </p>
              )}
            </div>
            <div className="form-field">
              <label htmlFor="cat-sort">並び順（小さい順に表示）</label>
              <input
                id="cat-sort"
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
              />
            </div>
            <label className="row" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                style={{ minHeight: "auto", width: 20, height: 20 }}
              />
              有効（レジ画面のカテゴリータブに表示する）
            </label>
            {!form.id && otherStores.length > 0 && (
              <div className="form-field">
                <label>他の店舗にも同じカテゴリーを作成する</label>
                <div className="stack" style={{ gap: 4 }}>
                  {otherStores.map((store) => (
                    <label key={store.id} className="row" style={{ gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={form.targetStoreIds.includes(store.id)}
                        onChange={(e) => toggleTargetStore(store.id, e.target.checked)}
                        style={{ minHeight: "auto", width: 20, height: 20 }}
                      />
                      {store.name}
                    </label>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: "4px 0 0" }}>
                  親カテゴリーが対象店舗になければ同じ名前で自動的に作成し、その下に作成します。
                </p>
              </div>
            )}
            <div className="row">
              <button className="btn" onClick={() => setForm(null)}>
                キャンセル
              </button>
              <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving || !form.name}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
